import { useEffect, useState } from 'react';
import { useLocation, Link } from 'react-router-dom';
import axios from 'axios';
import { X, MessageCircle, ShoppingBag, HelpCircle, Mail } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const HIDDEN_PREFIXES = ['/admin'];

/**
 * WhatsApp sales widget.
 *
 * Visible on all public pages (once admin enables it). Behaviour by user:
 *  - Not signed in: shows a friendly gated modal directing them to sign in,
 *    the FAQ, or email support.
 *  - Signed in, no paid order yet: same gated modal (only paid customers
 *    can reach sales on WhatsApp).
 *  - Signed in with at least one paid order: opens WhatsApp directly.
 */
const WhatsAppWidget = () => {
  const { pathname } = useLocation();
  const { user, token, isAuthenticated } = useAuth();

  const [config, setConfig] = useState({ whatsapp_enabled: false, whatsapp_number: '' });
  const [eligible, setEligible] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  // Load public checkout settings for the widget config
  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/checkout-settings`)
      .then((res) => {
        if (cancelled) return;
        setConfig({
          whatsapp_enabled: Boolean(res.data?.whatsapp_enabled),
          whatsapp_number: (res.data?.whatsapp_number || '').replace(/[^0-9+]/g, ''),
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // Check chat eligibility whenever the user changes
  useEffect(() => {
    if (!isAuthenticated || !token) {
      setEligible(false);
      return;
    }
    let cancelled = false;
    axios
      .get(`${API}/user/chat-eligibility`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!cancelled) setEligible(Boolean(res.data?.eligible));
      })
      .catch(() => {
        if (!cancelled) setEligible(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated, token, user?.id]);

  const hiddenByRoute = HIDDEN_PREFIXES.some((p) => pathname.startsWith(p));
  if (!config.whatsapp_enabled || hiddenByRoute) return null;

  const handleClick = () => {
    if (eligible) {
      const number = config.whatsapp_number.replace(/[^0-9]/g, '');
      const msg = encodeURIComponent(
        `Hi PACTAC team, I am a customer and I would like some help with my order.`
      );
      window.open(`https://wa.me/${number}?text=${msg}`, '_blank', 'noopener,noreferrer');
    } else {
      setModalOpen(true);
    }
  };

  return (
    <>
      {/* Floating bubble */}
      <button
        onClick={handleClick}
        data-testid="whatsapp-widget-btn"
        aria-label="Chat with PACTAC sales on WhatsApp"
        className="fixed z-[9999] bottom-6 right-6 group"
      >
        <span className="absolute inset-0 rounded-full bg-emerald-400/40 blur-xl group-hover:bg-emerald-400/60 transition-all"></span>
        <span className="relative flex items-center justify-center w-14 h-14 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 text-white shadow-lg shadow-emerald-500/40 hover:shadow-emerald-400/60 hover:scale-105 transition-all">
          <svg viewBox="0 0 24 24" className="w-7 h-7 fill-current" aria-hidden="true">
            <path d="M20.52 3.48A11.87 11.87 0 0012.06 0C5.49 0 .14 5.35.14 11.92c0 2.1.55 4.15 1.6 5.96L0 24l6.29-1.65a11.9 11.9 0 005.77 1.47h.01c6.57 0 11.92-5.35 11.92-11.92 0-3.19-1.24-6.19-3.47-8.42zM12.06 21.8h-.01a9.87 9.87 0 01-5.03-1.38l-.36-.21-3.73.98.99-3.64-.23-.37a9.87 9.87 0 01-1.51-5.26c0-5.46 4.44-9.9 9.9-9.9 2.64 0 5.13 1.03 7 2.9a9.83 9.83 0 012.9 7c-.01 5.46-4.45 9.88-9.92 9.88zm5.43-7.4c-.3-.15-1.76-.87-2.03-.97-.27-.1-.47-.15-.66.15-.2.3-.76.97-.93 1.16-.17.2-.34.22-.63.07-.3-.15-1.25-.46-2.38-1.47-.88-.79-1.47-1.75-1.64-2.04-.17-.3-.02-.46.13-.6.13-.13.3-.34.44-.51.15-.17.2-.3.3-.5.1-.2.05-.37-.02-.52-.07-.15-.66-1.6-.91-2.19-.24-.58-.48-.5-.66-.51h-.56c-.2 0-.52.07-.79.37-.27.3-1.04 1.02-1.04 2.48s1.06 2.87 1.21 3.07c.15.2 2.08 3.18 5.05 4.46.71.31 1.26.49 1.69.63.71.23 1.36.2 1.87.12.57-.08 1.76-.72 2-1.41.25-.7.25-1.29.17-1.41-.07-.13-.27-.2-.56-.34z"/>
          </svg>
        </span>
        <span className="pointer-events-none absolute right-full mr-3 top-1/2 -translate-y-1/2 whitespace-nowrap bg-white shadow-soft text-health-emerald text-xs font-bold px-3 py-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity">
          {eligible ? 'Chat with us' : 'Customer support'}
        </span>
      </button>

      {/* Gated modal */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-[10000] bg-health-ink/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => setModalOpen(false)}
          data-testid="whatsapp-gated-modal"
        >
          <div
            className="relative w-full max-w-md bg-white rounded-[2rem] shadow-lift overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute inset-0 bg-gradient-to-br from-health-mint to-white pointer-events-none"></div>
            <button
              onClick={() => setModalOpen(false)}
              className="absolute top-3 right-3 z-10 p-2 text-slate-600 hover:text-health-emerald transition-colors"
              aria-label="Close"
              data-testid="whatsapp-modal-close"
            >
              <X className="h-5 w-5" />
            </button>

            <div className="relative p-8">
              <div className="w-14 h-14 mb-5 rounded-2xl bg-health-mint flex items-center justify-center">
                <MessageCircle className="h-7 w-7 text-health-emerald" />
              </div>

              <h3 className="font-heading text-2xl font-bold text-health-ink mb-3 ">
                Reserved for our customers
              </h3>

              <p className="text-slate-700 text-sm leading-relaxed mb-2">
                Our WhatsApp line is where our sales team looks after people
                who have placed an order with PACTAC.
              </p>
              <p className="text-slate-600 text-sm leading-relaxed mb-6">
                {isAuthenticated
                  ? 'Once your first order is paid, this bubble unlocks and you can message us directly.'
                  : 'Sign in after your first purchase and the line opens up right here.'}
              </p>

              <div className="space-y-3">
                <Link
                  to="/products"
                  onClick={() => setModalOpen(false)}
                  className="flex items-center justify-between gap-3 px-4 py-3 rounded-2xl bg-health-emerald/10 hover:bg-health-emerald/15 transition-colors group"
                  data-testid="whatsapp-modal-shop-link"
                >
                  <span className="flex items-center gap-3 text-health-ink text-sm font-medium">
                    <ShoppingBag className="h-4 w-4 text-health-emerald" />
                    Browse products & place an order
                  </span>
                  <span className="text-health-emerald text-xs font-heading tracking-widest opacity-0 group-hover:opacity-100 transition-opacity">
                    GO
                  </span>
                </Link>

                <Link
                  to="/about-us"
                  onClick={() => setModalOpen(false)}
                  className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-health-line hover:border-health-emerald text-slate-700 hover:text-health-emerald text-sm transition-colors"
                  data-testid="whatsapp-modal-faq-link"
                >
                  <HelpCircle className="h-4 w-4 text-health-emerald" />
                  Read our FAQ & policies
                </Link>

                <a
                  href="mailto:hello@pactac.shop"
                  className="flex items-center gap-3 px-4 py-3 rounded-2xl border border-health-line hover:border-health-emerald text-slate-700 hover:text-health-emerald text-sm transition-colors"
                  data-testid="whatsapp-modal-email-link"
                >
                  <Mail className="h-4 w-4 text-health-coral" />
                  Email support: hello@pactac.shop
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WhatsAppWidget;
